/**
 * VerifiedMembers — R5 (2026-05-17) — PII-bearing report.
 *
 * Per R5-D5 (audit-amended):
 *   - Route gated by RequireRole (frontend) AND get_verified_members_report
 *     RPC enforces role server-side.
 *   - Every export action calls log_pii_export() to write a row to
 *     pii_export_audit (append-only, admin-readable).
 *
 * Per R5-D7 amended: "Credited rep present / unattributed" filter is
 * deferred to R5.1 (Sales by Rep report) — the verified-members report
 * presents the credited rep position ID inline so operators can see which
 * vouchers came in unattributed without a dedicated filter.
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getVerifiedMembersReport,
  resolvePositionNamesBulk,
  indexPositionsById,
  logPiiExport,
  type VerifiedMemberRow,
  type VerifiedMembersFilter,
} from "../../services/reportsSuite.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";
import VMCutoverBanner from "../../components/reports/VMCutoverBanner";
import { LockClosedIcon } from "@heroicons/react/24/outline";

const PAGE_SIZE = 50;

const formatDate = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const filterToRpc = (
  f: ReportFilterState,
  page: number,
  limit: number,
): VerifiedMembersFilter => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  state: f.state,
  eventType: f.type,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  page,
  limit,
});

const VerifiedMembers: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.state,
    filters.type,
    filters.orgId,
  ]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-verified-members", filters, page],
    queryFn: () =>
      getVerifiedMembersReport(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Hydrate credited-rep names
  const positionIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((r: any) => r.credited_rep_position_id)
            .filter((id: any): id is string => Boolean(id)),
        ),
      ),
    [rows],
  );

  const { data: positionRows } = useQuery({
    queryKey: ["verified-members-positions", positionIds],
    queryFn: () => resolvePositionNamesBulk(positionIds),
    enabled: positionIds.length > 0,
  });

  const positionsById = useMemo(
    () => indexPositionsById(positionRows ?? []),
    [positionRows],
  );

  const repDisplay = (positionId: string | null) => {
    if (!positionId) {
      return "Unattributed";
    }
    const p = positionsById.get(positionId);
    if (!p) {
      return positionId;
    }
    const label = p.assigned_name || p.title;
    return p.is_active ? label : `${label} (inactive)`;
  };

  const EXPORT_COLUMNS = [
    "Source",
    "Voucher ID",
    "Event ID",
    "Event Name",
    "Event Date",
    "Contact First Name",
    "Contact Last Name",
    "Contact Email",
    "Contact Phone",
    "Member Number",
    "Credited Rep",
    "Voucher Status",
    "Voucher Created",
    "Zuora Confirmed",
  ];

  // Post-ship audit should-fix: PII audit log must succeed BEFORE the
  // operator gets the file. Previously the log ran in a useEffect after
  // the export completed, so a logPiiExport() failure silently swallowed
  // the audit row while the export still downloaded. Now mapRows itself
  // calls logPiiExport — if it throws, ExportButton catches and the
  // operator sees a toast instead of a silent download.
  const mapRows = async (
    pageRows: VerifiedMemberRow[],
  ): Promise<Record<string, unknown>[]> => {
    // Bulk-hydrate rep names against pageRows (must-fix #2: don't rely
    // on the visible-page positionsById cache for export).
    const ids = Array.from(
      new Set(
        pageRows
          .map((r) => r.credited_rep_position_id)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    const positionRowsExport = await resolvePositionNamesBulk(ids);
    const exportPositionsById = indexPositionsById(positionRowsExport);
    const exportRepDisplay = (positionId: string | null) => {
      if (!positionId) {
        return "Unattributed";
      }
      const p = exportPositionsById.get(positionId);
      if (!p) {
        return positionId;
      }
      const label = p.assigned_name || p.title;
      return p.is_active ? label : `${label} (inactive)`;
    };

    // Log BEFORE returning rows — if this throws, ExportButton's
    // try/catch surfaces a toast and the file does not download.
    await logPiiExport(
      "verified_members",
      pageRows.length,
      filters as unknown as Record<string, unknown>,
    );

    return pageRows.map((r) => ({
      Source: r.source_type ?? "voucher",
      "Voucher ID": r.voucher_id ?? "",
      "Event ID": r.event_id,
      "Event Name": r.event_title ?? "",
      "Event Date": formatDate(r.event_date),
      "Contact First Name": r.contact_first_name ?? "",
      "Contact Last Name": r.contact_last_name ?? "",
      "Contact Email": r.contact_email ?? "",
      "Contact Phone": r.contact_phone ?? "",
      "Member Number": r.member_number ?? "",
      "Credited Rep": exportRepDisplay(r.credited_rep_position_id),
      "Voucher Status": r.voucher_status ?? "",
      "Voucher Created": formatDate(r.voucher_created_at),
      "Zuora Confirmed":
        r.is_zuora_confirmed === undefined || r.is_zuora_confirmed === null
          ? ""
          : r.is_zuora_confirmed
            ? "yes"
            : "pending",
    }));
  };

  // Plain fetchPage — no longer wraps the audit log (mapRows does it now).
  const wrappedFetchPage = async (
    f: ReportFilterState,
    p: number,
    l: number,
  ) => {
    const res = await getVerifiedMembersReport(filterToRpc(f, p, l));
    return { rows: res.rows, total: res.total };
  };

  return (
    <div className="space-y-4">
      <VMCutoverBanner />
      <ReportPageHeader
        title="Verified Members"
        description="Vouchers issued to identified contacts. Every export is logged to the PII audit log."
        badges={
          <span className="inline-flex items-center gap-1 rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
            <LockClosedIcon className="h-3 w-3" />
            PII
          </span>
        }
        actions={
          <ExportButton
            filters={filters}
            fetchPage={wrappedFetchPage}
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="verified-members"
          />
        }
      />

      <ReportFiltersBar filters={filters} onChange={setFilters} />

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Member</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Phone</th>
              <th className="px-3 py-2">Member #</th>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">Event Date</th>
              <th className="px-3 py-2">Credited Rep</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  No matching verified members.
                </td>
              </tr>
            )}
            {rows.map((r: any) => (
              <tr
                key={`${r.source_type ?? "voucher"}-${r.contact_id}-${r.event_id}`}
                className="hover:bg-gray-50"
              >
                <td className="px-3 py-2">
                  {r.source_type === "attendee" ? (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                      title={
                        r.is_zuora_confirmed
                          ? "QR-attributed signup confirmed by Zuora"
                          : "QR-attributed signup; Zuora confirmation pending"
                      }
                    >
                      Attendee
                      {r.is_zuora_confirmed === false && " (pending)"}
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
                      title="Legacy voucher-based verified member"
                    >
                      Voucher
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {[r.contact_first_name, r.contact_last_name]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </td>
                <td className="px-3 py-2">{r.contact_email || "—"}</td>
                <td className="px-3 py-2">{r.contact_phone || "—"}</td>
                <td className="px-3 py-2">{r.member_number || "—"}</td>
                <td className="px-3 py-2">{r.event_title || "—"}</td>
                <td className="px-3 py-2">{formatDate(r.event_date)}</td>
                <td className="px-3 py-2">
                  {repDisplay(r.credited_rep_position_id)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} total`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifiedMembers;
